import React, { ElementType, ReactNode, useMemo, useState } from 'react';
import MenuItem from '../../MenuItem/MenuItem';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { roleAccessState } from '../../../../../../../atoms/auth/auth.atom';
import { hasRoleAccess } from '../../../../../../../utils/access/access';
import { HttpMethod } from '../../../../../../../domain/Role';
import styles from './ModalFormAction.module.scss';
import { FormContentProps } from '../../../../../../List/ListForm/FormContent/FormContent';
import {
  ListTableContentProps,
  SpecificValues,
} from '../../../../../../List/ListTable/ListTableContent/ListTableContent';
import { ListTabVariant } from '../../../../../../List/ListTabs/ListTabs';
import { listRefreshNeededState } from '../../../../../../../atoms/list/list.atom';
import { formSubmitState } from '../../../../../../../atoms/form/form.atom';
import {
  InputValueType,
  SetValues,
} from '../../../../../../../hooks/useForm/useForm';
import ContentDialog from '../../../../../../Dialog/ContentDialog/ContentDialog';
import { useMutation } from '@tanstack/react-query';
import { DefaultError } from '@tanstack/query-core';
import axios from '../../../../../../../libraries/axios/axios';
import { Box, CircularProgress } from '@mui/material';

export type ModalFormActionAccess = {
  url: string;
  method: HttpMethod;
};

export type ModalFormActionContent = {
  Component: ElementType;
  props:
    | (FormContentProps & {
        variant: ListTabVariant.form;
      })
    | (ListTableContentProps & {
        variant: ListTabVariant.table;
      });
};

export type ModalFormActionProps = {
  icon?: ReactNode;
  text: string;
  modalActionContent: ModalFormActionContent;
  title?: string;
  subtitle?: string;
  baseApiUrl?: string;
  parentObject?: SetValues;
  displayActionButtons?: boolean;
  displayFormButtons?: boolean;
  onSuccess?: (data?: SetValues) => void;
  additionalParams?: any;
  parentObjectKey?: string;
  isSecure?: boolean;
  onValueUpdated?: (value: InputValueType) => void;
  access?: ModalFormActionAccess;
  callSuccessOnClose?: boolean;
  specificValues?: SpecificValues;
  onCloseApiUrl?: string;
};

export const ModalFormAction = ({
  icon,
  text,
  modalActionContent,
  title,
  subtitle,
  baseApiUrl,
  parentObject,
  displayActionButtons,
  displayFormButtons = false,
  onSuccess,
  additionalParams,
  isSecure,
  onValueUpdated,
  access,
  callSuccessOnClose,
  specificValues,
  onCloseApiUrl,
}: ModalFormActionProps) => {
  const roleAccessValue = useRecoilValue(roleAccessState);
  const setListRefreshNeededValue = useSetRecoilState(listRefreshNeededState);
  const setFormSubmitValue = useSetRecoilState(formSubmitState);

  const [isOpen, setIsOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const { mutate: closeApiCall, isPending: closePending } = useMutation<
    unknown,
    DefaultError
  >({
    mutationFn: () => {
      return axios.post(onCloseApiUrl ?? '');
    },
    onSuccess: () => {
      if (callSuccessOnClose) {
        handleSuccess();
      }

      setIsOpen(false);
    },
  });

  const handleSuccess = () => {
    if (!onSuccess) {
      setListRefreshNeededValue(true);
    } else {
      onSuccess?.();
    }

    setIsOpen(false);
  };

  const handleValueUpdated = (value: InputValueType) => {
    onValueUpdated?.(value);

    setIsOpen(false);
  };

  const ActiveComponent = useMemo(() => {
    const { Component, props } = modalActionContent;

    return (
      <Component
        {...props}
        additionalParams={additionalParams}
        isSubmitButtonVisible={displayFormButtons}
        onPending={setIsPending}
        onError={setError}
        defaultObject={parentObject}
        parentObject={parentObject}
        specificValues={specificValues}
        successBehavior={
          // @ts-ignore
          // eslint-disable-next-line react/prop-types
          props['successBehavior']
            ? {
                // @ts-ignore
                // eslint-disable-next-line react/prop-types
                ...props['successBehavior'],
                callback: (data?: SetValues) => {
                  handleSuccess();
                  // @ts-ignore
                  // eslint-disable-next-line react/prop-types
                  props?.successBehavior?.callback?.(data);
                },
              }
            : undefined
        }
        onValueUpdated={handleValueUpdated}
        onSuccess={onSuccess}
      />
    );
  }, [modalActionContent]);

  const isDisabled = useMemo(() => {
    if (access) {
      return !hasRoleAccess(roleAccessValue, access.method, access.url);
    }

    if (!baseApiUrl) {
      return false;
    }

    return !hasRoleAccess(
      roleAccessValue,
      HttpMethod.PATCH,
      `${baseApiUrl}/:id`,
    );
  }, [baseApiUrl, access]);

  const handleClick = (e: any) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  const handleClose = () => {
    if (onCloseApiUrl) {
      closeApiCall();
      return;
    }

    if (callSuccessOnClose) {
      handleSuccess();
    }

    setIsOpen(false);
  };

  return (
    <>
      <MenuItem
        onClick={handleClick}
        className={styles.menuItem}
        disabled={isDisabled}
      >
        {icon}
        {text}
      </MenuItem>
      <ContentDialog
        isOpen={isOpen}
        onClose={handleClose}
        onSubmit={() => {
          if (displayActionButtons === false) {
            return;
          }

          setFormSubmitValue(true);
        }}
        title={title}
        subtitle={subtitle}
        error={error}
        isDisabled={isPending}
        displayActionButtons={displayActionButtons}
        isSecure={isSecure}
        isLoading={isPending}
      >
        {closePending && (
          <Box className={styles.closeLoading}>
            <CircularProgress />
          </Box>
        )}
        {!closePending && ActiveComponent}
      </ContentDialog>
    </>
  );
};

export default ModalFormAction;
